import axios from "axios";
import { RCS_URL, RCHANS_URL, buildServiceParamMap, findDefaultChannelOfType, mergeById, sortByDate } from "../../common";

import { userInfo } from "../../common/user-info";
import { authHeader } from "../../common/auth-header";
import { relativeTimeThreshold } from "moment";
import jwtInterceptor  from '../../common/jwt-interceptor';

const state = {
  // allMessages: [],
  messageId: null,
  selectedMessage: null,
  commentsByShareId: {},
  repliesByShareId: {}, // Store all replies for a message
  repliesByCommentShareId: {}, // Store replies organized by parent comment share
  allMessagesByRallyId: {},
  commentsPaginationMeta: {} // Add pagination metadata
};

const getters = {
  allMessagesByRallyId: state => {
    //return sortByDate(state.allMessagesByRallyId[id], "attributes", "created-at")
    return state.allMessagesByRallyId;
  },
  messageId: state => state.messageId,
  selectedMessage: state => state.selectedMessage,
  commentsByShareId: state => id => {
    return state.commentsByShareId[id];
  },
  repliesByShareId: state => id => {
    return state.repliesByShareId[id] || [];
  },
  commentsPaginationMeta: state => shareId => {
    return state.commentsPaginationMeta[shareId] || {};
  }
};

const actions = {
  async clearMessageState({ commit, state }) {
    commit("clearState");
  },

  async fetchAMessageComments({ commit, state }, inparams) {
    const { shareId, channelId, pageSize = 3, loadMore = false } = inparams;
    const user = userInfo();

    // Get current pagination state
    const currentMeta = state.commentsPaginationMeta[shareId] || {};
    const currentPage = loadMore ? (currentMeta.currentPage || 0) + 1 : 1;

    // If loading more and we've already loaded all pages, return
    if (loadMore && currentMeta.totalPages && currentPage > currentMeta.totalPages) {
      return;
    }

    var jsonparams = {
      context_channel_id: parseInt(channelId),
      context_id: parseInt(shareId),
      context_type: "Share",
      content_type: "Message",
      include: "creator,creator_avatar,shares",
      sort: "contents.created_at desc",
      "page[size]": pageSize,
      "page[number]": currentPage
    };
    var params = buildServiceParamMap(jsonparams);
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      url: RCS_URL(),
      params
    };
    const response = await jwtInterceptor(requestOptions);

    // Calculate total pages from meta
    const totalCount = response.data.meta ? response.data.meta["total-count"] : 0;
    const totalPages = Math.ceil(totalCount / pageSize);

    // Store pagination metadata
    commit("setCommentsPaginationMeta", {
      shareId,
      totalCount: totalCount,
      totalPages: totalPages,
      currentPage: currentPage,
      pageSize: pageSize,
      hasMore: currentPage < totalPages
    });

    if (shareId) {
      const topLevelComments = response.data.data || [];

      // Store the top-level comments first
      commit("setCommentsByShareId", {
        id: shareId,
        comments: topLevelComments,
        append: loadMore
      });

      // Now fetch replies for all top-level comments we have so far
      const allStoredComments = state.commentsByShareId[shareId] || [];
      const commentShareIds = allStoredComments
          .map(comment => comment.relationships &&
              comment.relationships['context-share'] &&
              comment.relationships['context-share'].data &&
              comment.relationships['context-share'].data.id)
          .filter(id => id);

      // Fetch replies in batch if we have comment share IDs
      if (commentShareIds.length > 0) {
        await this.dispatch('messages/fetchRepliesBatch', {
          contextIds: commentShareIds,
          channelId: channelId,
          shareId: shareId
        });
      }
    }

    if (response.data.data && response.data.data.forEach) {
      response.data.data.forEach(c => {
        let cuser = {attributes:{}}
        cuser.attributes["first-name"] = c.attributes["creator-first-name"]
        cuser.attributes["last-name"] = c.attributes["creator-last-name"]
        cuser.attributes["avatar"] =  c.attributes["creator-avatar"]
        cuser.id = c.attributes["creator-id"] + ""
        this.dispatch("user/setUsersById", { users: [cuser]});
      })
    }
  },

  async fetchRepliesBatch({ commit, state }, inparams) {
    const { contextIds, channelId, shareId } = inparams;
    const user = userInfo();

    // Create params that include multiple context_ids
    var jsonparams = {
      context_channel_id: parseInt(channelId),
      context_id: contextIds, // Already an array as you corrected
      context_type: "Share",
      content_type: "Message",
      include: "creator,creator_avatar,shares",
      sort: "contents.created_at",
      "page[size]": 100 // Get all replies for these comments
    };

    var params = buildServiceParamMap(jsonparams);
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      url: RCS_URL(),
      params
    };

    try {
      const response = await jwtInterceptor(requestOptions);

      if (response.data && response.data.data) {
        const replies = response.data.data;

        // Create a map of replies by their context (which comment share they belong to)
        const repliesByCommentShareId = {};

        // Get the included shares to find context information
        const includedShares = response.data.included || [];
        const shareMap = {};
        includedShares.forEach(item => {
          if (item.type === 'shares') {
            shareMap[item.id] = item;
          }
        });

        replies.forEach(reply => {
          // Get the reply's share
          const replyShareId = reply.relationships &&
              reply.relationships['context-share'] &&
              reply.relationships['context-share'].data &&
              reply.relationships['context-share'].data.id;

          if (replyShareId && shareMap[replyShareId]) {
            // The context-id of the reply's share tells us which comment it belongs to
            const parentShareId = shareMap[replyShareId].attributes['context-id'];
            if (!repliesByCommentShareId[parentShareId]) {
              repliesByCommentShareId[parentShareId] = [];
            }
            repliesByCommentShareId[parentShareId].push(reply);
          }
        });

        // Store replies organized by parent comment share ID
        commit("setRepliesByCommentShareId", {
          shareId,
          repliesByCommentShareId
        });

        // Also store all replies for this message
        commit("setRepliesByShareId", {
          shareId,
          replies
        });

        // Process user data for replies
        replies.forEach(c => {
          let cuser = {attributes:{}}
          cuser.attributes["first-name"] = c.attributes["creator-first-name"]
          cuser.attributes["last-name"] = c.attributes["creator-last-name"]
          cuser.attributes["avatar"] =  c.attributes["creator-avatar"]
          cuser.id = c.attributes["creator-id"] + ""
          this.dispatch("user/setUsersById", { users: [cuser]});
        });

        return replies;
      }

      return [];
    } catch (error) {
      console.error("Error fetching batch replies:", error);
      return [];
    }
  },

  // New action for loading more comments with pagination
  async loadMoreComments({ dispatch }, inparams) {
    return dispatch('fetchAMessageComments', { ...inparams, loadMore: true });
  },

  async createReply({ commit, state, dispatch }, inparams) {
    const user = userInfo();
    const { rallyId, channelId, shareId, parentCommentId, body, parentCommentShareId } = inparams;
    //console.log("creating reply....")
    const requestOptions = {
      method: "POST",
      url: RCS_URL(),
      headers: {
        "Content-Type": "application/json"
      },
      data: {
        data: {
          type: "contents",
          attributes: {
            content_type: "Message",
            context_id: parentCommentShareId, // Use the parent comment's share ID
            context_channel_id: channelId,
            context_rally_id: rallyId,
            context_type: "Share", // Use Share as context type
            type: "Comment",
            body: body,
            creator_id: user.user_id,
            parent_comment_id: parentCommentId // Store parent reference
          },
          rally_id: rallyId,
          channel_id: "messages"
        }
      }
    };
    const response = await jwtInterceptor(requestOptions);

    // Instead of just refreshing with pageSize: 3, get the current number of loaded comments
    const currentComments = state.commentsByShareId[shareId] || [];
    const currentPageSize = Math.max(3, currentComments.length);

    // Refresh comments to show the new reply, preserving the current pagination
    await dispatch("fetchAMessageComments", {
      shareId,
      channelId,
      pageSize: currentPageSize
    });
  },

  //Parameters: {content"=>{"content_type"=>"Message", "context_id"=>"10134587", "context_type"=>"Share", "type"=>"Comment", "body"=>"another comment here..."}, "rally_id"=>"39017", "channel_id"=>"messages"}
  async createComment({ commit, state }, inparams) {
    const user = userInfo();
    const { rallyId, channelId } = inparams;
    //console.log("createcomments....")
    const requestOptions = {
      method: "POST",
      //url: "http://192.168.1.19:3020/api/v1/contents",
      url: RCS_URL(),
      headers: {
        "Content-Type": "application/json"
      },
      data: {
        data: {
          type: "contents",
          attributes: {
            content_type: "Message",
            context_id: inparams.shareId,
            context_channel_id: channelId,
            context_rally_id: rallyId,
            context_type: "Share",
            type: "Comment",
            body: inparams.body,
            creator_id: user.user_id
          },
          rally_id: rallyId,
          channel_id: "messages"
        }
      }
    };
    const response = await jwtInterceptor(requestOptions);

    // Reset pagination and refetch from beginning when new comment is added
    commit("resetCommentsPagination", { shareId: inparams.shareId });
    this.dispatch("messages/fetchAMessageComments", {shareId: inparams.shareId, channelId, pageSize: 3 });
  },

  async createMessage({ commit, state }, inparams) {
    //console.log("createmessage.....")
    const { priority, newMessage, channelId, status, rallyId, tempKey } = inparams;
    const user = userInfo();
    const createdAttributes = {
      priority,
      context_id: rallyId,
      status,
      context: "Rally",
      context_type: "Rally",
      title: newMessage.title,
      body: newMessage.body,
      context_channel_id: channelId,
      context_rally_id: rallyId,
      content_type: "Message",
      type: "WallMessage",
      include: "creator,creator_avatar",
      creator_id: user.user_id
    };
    const requestOptions = {
      method: "POST",
      url: RCS_URL(),
      headers: {
        "Content-Type": "application/json"
      },
      data: {
        data: {
          type: "contents",
          attributes: createdAttributes
        }
      }
    };

    //commit("setAllMessages", [{id:123, message: {attributes: createdAttributes}}]);
    const response = await jwtInterceptor(requestOptions);
    //we don't have the real info from the backend yet but when we are creating it then its editable and deletable by us...
    response.data.data.attributes["editable"] = true
    response.data.data.attributes["deletable"] = true
    response.data.data.attributes["creator-first-name"] = user.first_name
    response.data.data.attributes["creator-last-name"] = user.last_name
    this.dispatch("contents/setContentById", { id: response.data.data.id, content: response.data.data, tempKey });
  },

  async createDirectMessage({ commit, state }, inparams) {
    const { newMessage, channelId, status, rallyId, tempKey } = inparams;
    const user = userInfo();
    const createdAttributes = {
      context_id: channelId,
      context_type: "Channel",
      title: newMessage.title,
      body: newMessage.body,
      context_channel_id: channelId,
      context_rally_id: rallyId,
      content_type: "Message",
      type: "PrivateMessage",
      include: "creator,creator_avatar",
      creator_id: user.user_id
    };
    const requestOptions = {
      method: "POST",
      url: RCS_URL(),
      headers: {
        "Content-Type": "application/json"
      },
      data: {
        data: {
          type: "contents",
          attributes: createdAttributes
        }
      }
    };

    //commit("setAllMessages", [{id:123, message: {attributes: createdAttributes}}]);
    const response = await jwtInterceptor(requestOptions);
    this.dispatch("contents/setContentById", { id: response.data.data.id, content: response.data.data, tempKey });
  },

  async saveMessage({ commit, state, getters, rootGetters }, inparams) {
    const { message, rallyId, channelId, status, priority} = inparams;
    var usersById = rootGetters['user/usersById']
    const user = userInfo();
    const mid = message.id;
    const requestOptions = {
      method: "PUT",
      url: RCS_URL() + `/${mid}`,
      headers: {
        "Content-Type": "application/json"
      },
      data: {
        data: {
          type: "contents",
          id: mid,
          attributes: {
            id: mid,
            rally_id: rallyId,
            content_type: "Message",
            content_id: message.attributes["content_id"],
            title: message.attributes.title,
            body: message.attributes.body,
            type: "WallMessage",
            status,
            priority
          }
        }
      }
    };
    try {
      var response = await jwtInterceptor(requestOptions);
      var fetchParams = {
        rallyId: this.rallyId,
        contextType: "Channel",
        contextChannelId: channelId,
        contextId: channelId,
        pageNumber: 1,
        contentType: "Message",
        type: "WallMessage"
      };
      response.data.data.attributes.title = message.attributes.title
      response.data.data.attributes.body = message.attributes.body
      response.data.data.attributes.editable == true
      response.data.data.attributes.deletable == true
      message.attributes.editable = true
      message.attributes.deletable= true
      let u = usersById[response.data.data.attributes['creator-id']]
      if (u){
        response.data.data.attributes["creator-first-name"] = u.attributes['first-name']
        response.data.data.attributes["creator-last-name"] = u.attributes['last-name']
      }
      this.dispatch("contents/setAllContentByContextTypeAndId", {prefix: true, contextType:"", contextId:channelId, contents: [response.data.data]});
      //this.dispatch("contents/fetchContents", fetchParams);
      this.dispatch("contents/setContentById", { id: response.data.data.id, content: message});
      // this.dispatch("contents/setAllContentByContextTypeAndId", { contextType:"", contextId: channelId, contents: message});
    } catch (e) {
      console.log(e)
      //this._vm.$log.error(e);
    }
  },

  async addCommentToStore({ commit, state }, params) {
    commit("addACommentByShareId", params.comment);
  },

  // New action to remove a reply from the store
  async removeReplyFromStore({ commit, state }, { shareId, replyId }) {
    commit("removeReplyFromStore", { shareId, replyId });
  }
};

const mutations = {
  //clearAllMessages: state => (state.allMessages = []),
  clearState: state => {
    state.messageId = null;
    state.selectedMessage = null;
    state.commentsByShareId = {};
    state.repliesByShareId = {};
    state.repliesByCommentShareId = {};
    state.allMessagesByRallyId = {};
    state.commentsPaginationMeta = {};
  },
  setMessagesByRallyId: (state, params) => {
    const { rallyId, messages } = params;
    var curarr = state.allMessagesByRallyId[rallyId];
    if (!curarr) {
      curarr = [];
    }
    var theMessages = messages ? messages : [];
    var newarr = mergeById(curarr, theMessages, "id");
    var byIdObj = {};
    byIdObj[rallyId] = sortByDate(newarr, "attributes", "created-at");
    state.allMessagesByRallyId = { ...state.allMessagesByRallyId, ...byIdObj };
  },
  //setAllMessages: (state, messages) => (state.allMessages = mergeById([...state.allMessages], [...messages], "id")),
  setMessageId: (state, id) => (state.messageId = id),
  setSelectedMessage: (state, sel_message) => (state.selectedMessage = sel_message),
  addACommentByShareId: (state, c) => {
    var currarr = state.commentsByShareId[c.shareId];
    if (!currarr) {
      currarr = [];
    }
    var carr = [...currarr, c];
    var cobj = {};
    cobj[c.shareId] = sortByDate(carr, "attributes", "created-at");
    state.commentsByShareId = { ...state.commentsByShareId, ...cobj };
  },
  setCommentsByShareId: (state, c) => {
    const { id, comments, append = false } = c;
    if (append) {
      // Append to existing comments
      var existingComments = state.commentsByShareId[id] || [];
      var mergedComments = mergeById(existingComments, comments, "id");
      var cc = {};
      cc[id] = sortByDate(mergedComments, "attributes", "created-at");
      state.commentsByShareId = { ...state.commentsByShareId, ...cc };
    } else {
      // Replace comments
      var cc = {};
      cc[id] = sortByDate(comments, "attributes", "created-at");
      state.commentsByShareId = { ...state.commentsByShareId, ...cc };
    }
  },
  setCommentsPaginationMeta: (state, params) => {
    const { shareId, totalCount, totalPages, currentPage, pageSize, hasMore } = params;
    state.commentsPaginationMeta = {
      ...state.commentsPaginationMeta,
      [shareId]: {
        totalCount,
        totalPages,
        currentPage,
        pageSize,
        hasMore
      }
    };
  },
  appendCommentsByShareId: (state, params) => {
    const { shareId, comments } = params;
    const existingComments = state.commentsByShareId[shareId] || [];
    const mergedComments = mergeById(existingComments, comments, "id");

    var byIdObj = {};
    byIdObj[shareId] = sortByDate(mergedComments, "attributes", "created-at");
    state.commentsByShareId = { ...state.commentsByShareId, ...byIdObj };
  },
  resetCommentsPagination: (state, params) => {
    const { shareId } = params;
    if (state.commentsPaginationMeta[shareId]) {
      delete state.commentsPaginationMeta[shareId];
    }
  },
  setRepliesByShareId: (state, params) => {
    const { shareId, replies } = params;
    //console.log(`Storing ${replies.length} replies for share ${shareId}`);
    // Store replies for this message share
    state.repliesByShareId = {
      ...state.repliesByShareId,
      [shareId]: replies
    };
  },
  setRepliesByCommentShareId: (state, params) => {
    const { shareId, repliesByCommentShareId } = params;
    // Store the organized replies
    state.repliesByCommentShareId = {
      ...state.repliesByCommentShareId,
      [shareId]: repliesByCommentShareId
    };
    //console.log('Organized replies by comment share:', repliesByCommentShareId);
  },
  appendRepliesByShareId: (state, params) => {
    const { shareId, replies } = params;
    const existingComments = state.commentsByShareId[shareId] || [];

    // Filter out any duplicate replies
    const existingIds = existingComments.map(c => c.id);
    const newReplies = replies.filter(reply => !existingIds.includes(reply.id));

    if (newReplies.length > 0) {
      const mergedComments = [...existingComments, ...newReplies];
      var cc = {};
      cc[shareId] = sortByDate(mergedComments, "attributes", "created-at");
      state.commentsByShareId = { ...state.commentsByShareId, ...cc };
    }
  },

  // New mutation to remove a reply from the store
  removeReplyFromStore: (state, { shareId, replyId }) => {
    // Remove from organized replies
    const organizedReplies = state.repliesByCommentShareId[shareId];
    if (organizedReplies) {
      Object.keys(organizedReplies).forEach(commentShareId => {
        organizedReplies[commentShareId] = organizedReplies[commentShareId].filter(
            reply => reply.id !== replyId
        );
      });
    }

    // Remove from all replies
    const allReplies = state.repliesByShareId[shareId];
    if (allReplies) {
      state.repliesByShareId[shareId] = allReplies.filter(reply => reply.id !== replyId);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};